import { useEffect, useState } from 'react';
import { ShieldCheck, Desktop, DeviceMobile, Clock, MapPin, Trash, Warning, CheckCircle, ArrowClockwise, Copy, Check, ClockCounterClockwise, SignIn, SignOut, KeyReturn, X } from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

// ── helpers ──────────────────────────────────────────────────────────────────
function parseUA(ua) {
  if (!ua) return { browser: 'Onbekend', os: '' };
  const s = ua.toLowerCase();
  let browser = 'Browser';
  if (s.includes('edg/')) browser = 'Edge';
  else if (s.includes('chrome/')) browser = 'Chrome';
  else if (s.includes('firefox/')) browser = 'Firefox';
  else if (s.includes('safari/')) browser = 'Safari';

  let os = '';
  if (s.includes('windows')) os = 'Windows';
  else if (s.includes('mac os') || s.includes('macintosh')) os = 'macOS';
  else if (s.includes('linux')) os = 'Linux';
  else if (s.includes('iphone') || s.includes('ipad')) os = 'iOS';
  else if (s.includes('android')) os = 'Android';

  const mobile = s.includes('mobile') || s.includes('iphone') || s.includes('android');
  return { browser, os, mobile };
}

function relTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'Zojuist';
  if (diff < 3600) return `${Math.floor(diff / 60)} min geleden`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} uur geleden`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)} d geleden`;
  return d.toLocaleDateString('nl-NL');
}

function daysUntil(iso) {
  if (!iso) return 0;
  return Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000));
}

// ── Audit-log event metadata (icon, color, label) ────────────────────────────
const EVENT_META = {
  login_success: { icon: SignIn, color: 'emerald', label: 'Login geslaagd' },
  login_failed: { icon: X, color: 'red', label: 'Login mislukt' },
  login_locked_out: { icon: Warning, color: 'red', label: 'Wachtwoord vergrendeld (5 fouten)' },
  totp_success: { icon: ShieldCheck, color: 'emerald', label: '2FA-code correct' },
  totp_failed: { icon: X, color: 'amber', label: 'Onjuiste 2FA-code' },
  totp_locked_out: { icon: Warning, color: 'red', label: '2FA vergrendeld (5 fouten)' },
  recovery_code_used: { icon: KeyReturn, color: 'amber', label: 'Herstelcode gebruikt' },
  recovery_codes_regenerated: { icon: ArrowClockwise, color: 'blue', label: 'Herstelcodes opnieuw gegenereerd' },
  trusted_device_added: { icon: Desktop, color: 'blue', label: 'Apparaat vertrouwd' },
  trusted_device_skip: { icon: ShieldCheck, color: 'zinc', label: '2FA overgeslagen (vertrouwd)' },
  device_revoked: { icon: Trash, color: 'red', label: 'Apparaat ingetrokken' },
  devices_revoke_all: { icon: Trash, color: 'red', label: 'Alle apparaten ingetrokken' },
  twofa_enrolled: { icon: ShieldCheck, color: 'emerald', label: '2FA ingesteld' },
  logout: { icon: SignOut, color: 'zinc', label: 'Uitgelogd' },
};

const COLOR_CLASSES = {
  emerald: 'bg-emerald-50 text-emerald-700',
  red: 'bg-red-50 text-red-700',
  amber: 'bg-amber-50 text-amber-700',
  blue: 'bg-blue-50 text-blue-700',
  zinc: 'bg-zinc-100 text-zinc-600',
};

// ── Toast (simple inline) ────────────────────────────────────────────────────
function Toast({ kind, children }) {
  const base = "px-3 py-2 text-xs rounded-sm border flex items-center gap-2";
  const styles = kind === 'success'
    ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
    : kind === 'error'
    ? 'bg-red-50 border-red-200 text-red-800'
    : 'bg-blue-50 border-blue-200 text-blue-800';
  return <div className={`${base} ${styles}`}>{children}</div>;
}

export default function SecuritySettingsPage() {
  const { user } = useAuth();

  const [status, setStatus] = useState(null);
  const [devices, setDevices] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  // Regenerate recovery codes
  const [showRegen, setShowRegen] = useState(false);
  const [regenCode, setRegenCode] = useState('');
  const [regenBusy, setRegenBusy] = useState(false);
  const [newRecoveryCodes, setNewRecoveryCodes] = useState(null);
  const [codesCopied, setCodesCopied] = useState(false);

  const [confirmRevoke, setConfirmRevoke] = useState(null); // device_id or 'all'

  const fireToast = (kind, msg) => {
    setToast({ kind, msg });
    setTimeout(() => setToast(null), 3500);
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      const [sRes, dRes, aRes] = await Promise.all([
        fetch(`${API}/api/auth/2fa/status`, { credentials: 'include' }),
        fetch(`${API}/api/auth/2fa/devices`, { credentials: 'include' }),
        fetch(`${API}/api/auth/security/audit-log?limit=50`, { credentials: 'include' }),
      ]);
      if (sRes.ok) setStatus(await sRes.json());
      if (dRes.ok) setDevices((await dRes.json()).devices || []);
      if (aRes.ok) setAuditLog((await aRes.json()).events || []);
    } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { loadAll(); }, []);

  const revokeDevice = async (deviceId) => {
    try {
      const r = await fetch(`${API}/api/auth/2fa/devices/${deviceId}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (r.ok) {
        fireToast('success', 'Apparaat ingetrokken');
        await loadAll();
      } else {
        fireToast('error', 'Intrekken mislukt');
      }
    } catch {
      fireToast('error', 'Verbindingsfout');
    }
    setConfirmRevoke(null);
  };

  const revokeAll = async () => {
    try {
      const r = await fetch(`${API}/api/auth/2fa/devices/revoke-all`, {
        method: 'POST', credentials: 'include',
      });
      if (r.ok) {
        const d = await r.json();
        fireToast('success', `${d.revoked || 0} apparaten ingetrokken`);
        await loadAll();
      } else {
        fireToast('error', 'Intrekken mislukt');
      }
    } catch {
      fireToast('error', 'Verbindingsfout');
    }
    setConfirmRevoke(null);
  };

  const regenerateRecovery = async () => {
    setRegenBusy(true);
    try {
      const r = await fetch(`${API}/api/auth/2fa/recovery-codes/regenerate`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: regenCode }),
      });
      if (r.ok) {
        const d = await r.json();
        setNewRecoveryCodes(d.recovery_codes || []);
        setRegenCode('');
        fireToast('success', 'Nieuwe herstelcodes gegenereerd');
        await loadAll();
      } else {
        fireToast('error', 'Ongeldige TOTP code');
      }
    } catch {
      fireToast('error', 'Verbindingsfout');
    }
    setRegenBusy(false);
  };

  const copyCodes = async () => {
    if (!newRecoveryCodes) return;
    try {
      await navigator.clipboard.writeText(newRecoveryCodes.join('\n'));
      setCodesCopied(true);
      setTimeout(() => setCodesCopied(false), 2000);
    } catch { /* ignore */ }
  };

  const downloadCodes = () => {
    if (!newRecoveryCodes) return;
    const blob = new Blob(
      [`PoolFIX — 2FA Recovery Codes\nAccount: ${user?.email}\nGegenereerd: ${new Date().toISOString()}\n\n${newRecoveryCodes.join('\n')}\n\nBewaar deze codes op een veilige plek. Elke code werkt één keer.`],
      { type: 'text/plain' },
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `poolfix-recovery-codes-${user?.email}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-4xl mx-auto p-6 md:p-8" data-testid="security-settings-page">
      <div className="mb-8">
        <h1 className="font-heading text-3xl font-bold tracking-tight text-[#0F172A]">Beveiliging</h1>
        <p className="text-sm text-zinc-500 mt-1">Tweestapsverificatie en vertrouwde apparaten beheren</p>
      </div>

      {toast && (
        <div className="mb-4" data-testid="security-toast">
          <Toast kind={toast.kind}>{toast.msg}</Toast>
        </div>
      )}

      {/* 2FA Status Card */}
      <div className="mb-6 bg-white border border-zinc-200 rounded-sm p-5" data-testid="twofa-status-card">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-sm bg-emerald-50 flex items-center justify-center flex-shrink-0">
              <ShieldCheck size={20} className="text-emerald-600" />
            </div>
            <div>
              <h3 className="font-heading text-lg font-semibold text-[#0F172A]">Tweestapsverificatie</h3>
              <p className="text-xs text-zinc-500 mt-0.5">
                {status?.enabled ? (
                  <>Actief sinds {status.enabled_at ? new Date(status.enabled_at).toLocaleDateString('nl-NL') : '—'}</>
                ) : (
                  <>Niet ingesteld</>
                )}
              </p>
              <div className="mt-3 flex items-center gap-2 text-xs">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-emerald-100 text-emerald-800 font-medium" data-testid="twofa-enabled-badge">
                  <CheckCircle size={12} weight="fill" /> Actief
                </span>
                <span className="text-zinc-500">
                  {status?.unused_recovery_codes ?? '—'} ongebruikte herstelcode{status?.unused_recovery_codes === 1 ? '' : 's'}
                </span>
              </div>
            </div>
          </div>

          {status?.enabled && (
            <button
              type="button"
              data-testid="open-regen-recovery-btn"
              onClick={() => { setShowRegen(v => !v); setNewRecoveryCodes(null); setRegenCode(''); }}
              className="text-xs px-3 py-2 border border-zinc-200 rounded-sm hover:bg-zinc-50 transition-colors flex items-center gap-1 whitespace-nowrap"
            >
              <ArrowClockwise size={12} /> Herstelcodes opnieuw genereren
            </button>
          )}
        </div>

        {/* Recovery regeneration panel */}
        {showRegen && (
          <div className="mt-5 pt-5 border-t border-zinc-100" data-testid="regen-recovery-panel">
            {!newRecoveryCodes ? (
              <>
                <p className="text-xs text-zinc-600 mb-3">
                  Voer de huidige 6-cijferige code uit je authenticator-app in.
                  De bestaande herstelcodes worden daarna <strong>direct ongeldig</strong>.
                </p>
                <div className="flex gap-2">
                  <input
                    data-testid="regen-totp-input"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="123456"
                    value={regenCode}
                    onChange={e => setRegenCode(e.target.value.replace(/\D/g, ''))}
                    className="flex-1 px-3 py-2 text-sm font-data tracking-wider text-center border border-zinc-200 rounded-sm focus:outline-none focus:border-[#0F172A]"
                  />
                  <button
                    type="button"
                    data-testid="regen-confirm-btn"
                    onClick={regenerateRecovery}
                    disabled={regenBusy || regenCode.length !== 6}
                    className="px-4 py-2 text-xs bg-[#0F172A] text-white rounded-sm hover:bg-[#1E293B] disabled:opacity-40 transition-colors"
                  >
                    {regenBusy ? 'Bezig...' : 'Genereer'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-sm text-xs text-amber-900">
                  <Warning size={14} weight="bold" className="inline mr-1" />
                  Bewaar deze 8 codes op een veilige plek. Ze worden maar <strong>één keer</strong> getoond.
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 p-3 bg-zinc-50 border border-zinc-200 rounded-sm">
                  {newRecoveryCodes.map((c, i) => (
                    <code key={i} className="text-xs font-data text-zinc-800 tracking-wider" data-testid={`new-recovery-code-${i}`}>
                      {c}
                    </code>
                  ))}
                </div>
                <div className="flex gap-2 mt-3">
                  <button
                    type="button"
                    onClick={copyCodes}
                    data-testid="copy-new-codes-btn"
                    className="flex-1 px-3 py-2 text-xs border border-zinc-200 rounded-sm hover:bg-zinc-50 transition-colors flex items-center justify-center gap-1"
                  >
                    {codesCopied ? <><Check size={12} /> Gekopieerd</> : <><Copy size={12} /> Kopieer alles</>}
                  </button>
                  <button
                    type="button"
                    onClick={downloadCodes}
                    data-testid="download-new-codes-btn"
                    className="flex-1 px-3 py-2 text-xs border border-zinc-200 rounded-sm hover:bg-zinc-50 transition-colors"
                  >
                    Download .txt
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Trusted Devices Card */}
      <div className="bg-white border border-zinc-200 rounded-sm p-5" data-testid="trusted-devices-card">
        <div className="flex items-center justify-between gap-4 mb-4">
          <div>
            <h3 className="font-heading text-lg font-semibold text-[#0F172A]">Vertrouwde apparaten</h3>
            <p className="text-xs text-zinc-500 mt-0.5">
              Deze browsers slaan 2FA over — tot 30 dagen vanaf het moment dat je ze vertrouwde
            </p>
          </div>
          {devices.length > 0 && (
            <button
              type="button"
              data-testid="revoke-all-devices-btn"
              onClick={() => setConfirmRevoke('all')}
              className="text-xs px-3 py-2 border border-red-200 text-red-700 rounded-sm hover:bg-red-50 transition-colors flex items-center gap-1 whitespace-nowrap"
            >
              <Trash size={12} /> Alle intrekken
            </button>
          )}
        </div>

        {loading ? (
          <div className="text-center py-8 text-xs text-zinc-500">Laden...</div>
        ) : devices.length === 0 ? (
          <div className="text-center py-12 text-sm text-zinc-500" data-testid="no-devices-empty">
            Geen vertrouwde apparaten.<br />
            <span className="text-xs text-zinc-400">
              Vink bij je volgende login "Vertrouw dit apparaat 30 dagen" aan om deze lijst te vullen.
            </span>
          </div>
        ) : (
          <div className="divide-y divide-zinc-100">
            {devices.map(dev => {
              const ua = parseUA(dev.user_agent);
              const days = daysUntil(dev.trusted_until);
              const nearExpiry = days < 5;
              return (
                <div
                  key={dev.device_id}
                  data-testid={`device-row-${dev.device_id}`}
                  className="py-4 flex items-start justify-between gap-4"
                >
                  <div className="flex items-start gap-3 min-w-0 flex-1">
                    <div className="w-10 h-10 rounded-sm bg-zinc-100 flex items-center justify-center flex-shrink-0">
                      {ua.mobile
                        ? <DeviceMobile size={18} className="text-zinc-600" />
                        : <Desktop size={18} className="text-zinc-600" />
                      }
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-[#0F172A] truncate">
                          {ua.browser} {ua.os && <span className="text-zinc-500">· {ua.os}</span>}
                        </span>
                        {dev.is_current && (
                          <span
                            data-testid={`device-current-badge-${dev.device_id}`}
                            className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider font-medium rounded-sm bg-[#0055FF]/10 text-[#0055FF]"
                          >
                            <CheckCircle size={10} weight="fill" /> Dit apparaat
                          </span>
                        )}
                        {nearExpiry && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider font-medium rounded-sm bg-amber-50 text-amber-800">
                            <Warning size={10} weight="fill" /> Verloopt binnenkort
                          </span>
                        )}
                      </div>
                      <div className="mt-1 flex items-center gap-3 text-xs text-zinc-500 flex-wrap">
                        <span className="inline-flex items-center gap-1">
                          <Clock size={11} /> Laatst gebruikt: {relTime(dev.last_used_at || dev.created_at)}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <MapPin size={11} /> {dev.ip || 'onbekend'}
                        </span>
                        <span className="text-zinc-400">
                          Verloopt in {days} dag{days === 1 ? '' : 'en'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <button
                    type="button"
                    data-testid={`revoke-device-btn-${dev.device_id}`}
                    onClick={() => setConfirmRevoke(dev.device_id)}
                    className="text-xs px-3 py-1.5 border border-zinc-200 text-zinc-600 rounded-sm hover:bg-red-50 hover:border-red-200 hover:text-red-700 transition-colors flex items-center gap-1 flex-shrink-0"
                  >
                    <Trash size={12} /> Intrekken
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Audit Log Card */}
      <div className="mt-6 bg-white border border-zinc-200 rounded-sm p-5" data-testid="audit-log-card">
        <div className="flex items-center justify-between gap-4 mb-4">
          <div className="flex items-center gap-2">
            <ClockCounterClockwise size={18} className="text-zinc-500" />
            <h3 className="font-heading text-lg font-semibold text-[#0F172A]">Activiteitenlog</h3>
          </div>
          <span className="text-xs text-zinc-500">Laatste {auditLog.length} gebeurtenissen</span>
        </div>

        {auditLog.length === 0 ? (
          <div className="text-center py-12 text-sm text-zinc-500" data-testid="audit-empty">
            Nog geen beveiligingsactiviteit geregistreerd.
          </div>
        ) : (
          <div className="divide-y divide-zinc-100 -mx-2">
            {auditLog.map((e, idx) => {
              const meta = EVENT_META[e.event_type] || { icon: Clock, color: 'zinc', label: e.event_type };
              const Icon = meta.icon;
              const ua = parseUA(e.user_agent);
              return (
                <div
                  key={idx}
                  data-testid={`audit-row-${idx}`}
                  className="flex items-start gap-3 px-2 py-2.5 hover:bg-zinc-50/50 transition-colors"
                >
                  <div className={`w-7 h-7 rounded-sm flex items-center justify-center flex-shrink-0 ${COLOR_CLASSES[meta.color] || COLOR_CLASSES.zinc}`}>
                    <Icon size={13} weight="bold" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-[#0F172A]" data-testid={`audit-label-${idx}`}>
                        {meta.label}
                      </span>
                      {e.metadata?.context && (
                        <span className="text-[10px] uppercase tracking-wider text-zinc-400 bg-zinc-100 px-1.5 py-0.5 rounded-sm">
                          {e.metadata.context}
                        </span>
                      )}
                      {typeof e.metadata?.attempt === 'number' && (
                        <span className="text-[10px] uppercase tracking-wider text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded-sm">
                          poging {e.metadata.attempt}/5
                        </span>
                      )}
                    </div>
                    <div className="mt-0.5 flex items-center gap-3 text-xs text-zinc-500 flex-wrap">
                      <span className="inline-flex items-center gap-1">
                        <Clock size={10} /> {relTime(e.timestamp)}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <MapPin size={10} /> {e.ip || 'onbekend'}
                      </span>
                      {ua.browser !== 'Onbekend' && (
                        <span className="text-zinc-400">
                          {ua.browser}{ua.os ? ` · ${ua.os}` : ''}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Confirm dialog */}
      {confirmRevoke && (
        <div
          className="fixed inset-0 bg-zinc-900/40 flex items-center justify-center p-4 z-50"
          data-testid="revoke-confirm-overlay"
          onClick={() => setConfirmRevoke(null)}
        >
          <div
            className="bg-white rounded-sm border border-zinc-200 shadow-lg p-5 max-w-sm w-full"
            onClick={e => e.stopPropagation()}
          >
            <h4 className="font-heading text-base font-semibold text-[#0F172A] mb-2">
              {confirmRevoke === 'all' ? 'Alle vertrouwde apparaten intrekken?' : 'Dit apparaat intrekken?'}
            </h4>
            <p className="text-xs text-zinc-600 mb-5">
              {confirmRevoke === 'all'
                ? 'Alle browsers zullen bij de volgende login weer een 2FA-code moeten invoeren.'
                : 'Deze browser zal bij de volgende login weer een 2FA-code moeten invoeren.'}
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                data-testid="revoke-confirm-cancel-btn"
                onClick={() => setConfirmRevoke(null)}
                className="flex-1 px-3 py-2 text-xs border border-zinc-200 rounded-sm hover:bg-zinc-50 transition-colors"
              >
                Annuleer
              </button>
              <button
                type="button"
                data-testid="revoke-confirm-btn"
                onClick={() => confirmRevoke === 'all' ? revokeAll() : revokeDevice(confirmRevoke)}
                className="flex-1 px-3 py-2 text-xs bg-red-600 text-white rounded-sm hover:bg-red-700 transition-colors"
              >
                Ja, intrekken
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
